import { AlignHorizontalLeft, AssessmentOutlined, GroupAdd, MonetizationOnOutlined } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { BaseQueryFn, MutationDefinition, QueryDefinition } from "@reduxjs/toolkit/dist/query";
import { LazyQueryTrigger, MutationTrigger } from "@reduxjs/toolkit/dist/query/react/buildHooks";
import { enqueueSnackbar } from "notistack";
import { CustomCost, formatCurrency, type ProcessedData, type AssemblyProcessCost, CostGroup } from "pdf-generator";
import { AppDispatch } from "../../redux";
import { closeBackdrop } from "../../redux/slices/backdrop";
import { closeConfirmationDialog } from "../../redux/slices/confirmationDialog";
import { PricingSliceInterface } from "../../redux/slices/pricing";

export const NavBars = (pricingSubPaths: string[], isEdit: boolean = false) => ([
    {
        id: "1",
        title: "Basic Details",
        icon: <AlignHorizontalLeft fontSize="small" />,
        path: pricingSubPaths[0]
    },
    {
        id: "2",
        title: "Master Pricing",
        disabled: !isEdit,
        icon: <MonetizationOnOutlined fontSize="small" />,
        path: pricingSubPaths[1]
    },
    {
        id: "3",
        title: "Report",
        disabled: !isEdit,
        icon: <AssessmentOutlined fontSize="small" />,
        path: pricingSubPaths[2]
    },
    {
        id: "4",
        title: "Manage Users",
        disabled: !isEdit,
        icon: <GroupAdd fontSize="small" />,
        path: pricingSubPaths[3]
    },
])

export const StatusOptions = [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }];

export const PricingModelOptions = [
    { value: "markup:individual", label: "Markup Model" },
]

export const PricingTabs: string[] = ["edit", "preview", "flatView"];

export const OperationTypes = [
    { value: true, label: "Subtract" },
    { value: false, label: "Add" },
]

export const CostingTypes = [
    { value: "hr", label: "Per Hour" },
    { value: "day", label: "Per Day" },
    { value: "month", label: "Per Month" },
    { value: "fixed", label: "Fixed" },
]

export const mapTreeToPriceCost = (bom: any) => {
    // Transform assembly process cost
    function transformAssmProcs(assmProcs: any) {
        return assmProcs.map((proc: any) => ({
            // assm_procs: {
            //     child_id: proc.child_id,
            //     proc_name: proc.proc_name,
            //     unit_cost: proc.unit_cost?.cost ?? 0,
            //     quantity: proc.quantity
            // },
            _id: proc?.child_id,
            child_id: proc?.child_id,
            markup_percentage: 0, // default value
            custom_costs: [],
        }));
    }

    // Transform material costs
    function transformMaterials(materials: any) {
        return materials.map((material: any) => ({
            // ...material,
            _id: material._id,
            child_id: material?.child_id,
            markup_cost: undefined, // default value
            markup_percentage: 0, // default value
            total_custom_cost_fixed: undefined, // default value
            total_custom_cost_percentage: undefined, // default value
            total_custom_cost: undefined, // default value
            custom_costs: [],
            sequence_id: material.sequence_id,
            version: material?.version ?? 0,
            short_description: material?.material?.short_description,
            validation_errors: undefined,
        }));
    }

    // Recursively transform children
    function transformChildren(children: any) {
        return children.map((child: any) => ({
            // ...child,
            _id: child._id,
            child_id: child?.child_id,
            assembly_process_costs: transformAssmProcs(child?.bom?.assm_procs ?? []),
            material_costs: transformMaterials(child?.bom?.bom_materials || []),
            children_costs: transformChildren(child?.bom?.bom_children || []),
            sequence_id: child.sequence_id,
            version: child?.version ?? 0,
            short_description: child.bom?.short_description,
            validation_errors: undefined,
        }));
    }

    return {
        // ...bom,
        _id: bom._id,
        child_id: bom?.child_id ?? "000000000000000000000000",
        assembly_process_costs: transformAssmProcs(bom?.assm_procs ?? []),
        material_costs: transformMaterials(bom?.bom_materials ?? []),
        children_costs: transformChildren(bom?.bom_children || []),
        sequence_id: bom.sequence_id,
        version: bom?.version ?? 0,
        short_description: bom.short_description,
    };
}

export const updateTreeWithFlattenedData = (tree: any, flattenedData: any[], columns: GridColDef[]) => {
    // Helper function to find the flattened data by child_id
    function findFlattenedDataById(child_id: any) {
        return flattenedData.find((item: any) => item?.child_id === child_id);
    }

    // Recursive function to update and return a new copy of tree nodes
    function recurseAndUpdate(treeNode: any, level = 0): any {
        // Find the corresponding flattened data for the current node by child_id
        const flattenedNode = findFlattenedDataById(treeNode?.child_id) || {};

        // Update fields based on the flattened data
        let updatedNode = { ...treeNode };

        if (flattenedNode || flattenedData.length <= 0) {
            let custom_costs = Object.keys(flattenedNode).filter(key => key.includes("custom_")).map((key, idx: number) => {
                const indexOF = columns.findIndex(c => c.field === key);
                return { ...flattenedNode[key], position: indexOF, value: flattenedNode[key]?.value ?? 0, amount: undefined };
            }).filter(a => a.position >= 0).sort((a, b) => a.position - b.position)
            if (flattenedNode.bomId?.includes("BOM") || updatedNode?.sequence_id?.includes("BOM")) {
                updatedNode = {
                    ...updatedNode,
                    markup_percentage: flattenedNode.markup_percentage || 0,
                    bom_cost: undefined,
                    normal_quantity: undefined,
                    override_cost: undefined,
                    spare_part_quantity: undefined,
                    sum_of_assm_procs_total_cost: updatedNode?.sum_of_assm_procs_total_cost || undefined,
                    total_cost: undefined,
                    final_total_cost_in_parent_currency: undefined,
                    final_total_cost_in_pricing_currency: undefined,
                    total_quantity: undefined,
                    unit_cost: undefined,
                    final_total_cost: undefined,
                    sum_of_assm_procs_final_total_cost: undefined,
                    sum_of_assm_procs_final_total_cost_in_parent_currency: undefined,
                    sum_of_children_final_total_cost_in_parent_currency: undefined,
                    sum_of_children_final_total_cost_in_pricing_currency: undefined,
                    sum_of_material_final_total_cost_in_parent_currency: undefined,
                    markup_cost_in_pricing_currency: undefined,
                    total_cost_in_pricing_currency: undefined,
                    total_custom_cost_in_pricing_currency: undefined,
                    unit_cost_in_pricing_currency: undefined,
                    validation_errors: undefined,
                    notes: undefined,
                    assembly_process_costs: updatedNode.assembly_process_costs?.map((a: any) => {
                        return ({
                            ...a,
                            assembly_process_costs: a?.assembly_process_costs || undefined,
                            markup_cost: undefined,
                            proc_name: undefined,
                            quantity: undefined,
                            total_cost: undefined,
                            total_custom_cost: undefined,
                            total_custom_cost_fixed: undefined,
                            total_custom_cost_percentage: undefined,
                            unit_cost: undefined,
                            markup_percentage: flattenedNode.markup_percentage || 0,
                            final_total_cost: undefined,
                            final_total_cost_in_parent_currency: undefined,
                            final_total_cost_in_pricing_currency: undefined,
                            notes: undefined,
                            validation_errors: undefined,
                            markup_cost_in_pricing_currency: undefined,
                            total_cost_in_pricing_currency: undefined,
                            total_custom_cost_in_pricing_currency: undefined,
                            unit_cost_in_pricing_currency: undefined,
                            total_custom_cost_margin_ratio: undefined,
                            custom_costs,
                            _id: a?.child_id,
                        });
                    }),
                };
            } else {
                updatedNode = {
                    ...updatedNode,
                    markup_percentage: flattenedNode.markup_percentage || 0,
                    markup_cost: undefined,
                    material_cost: undefined,
                    normal_quantity: undefined,
                    override_cost: undefined,
                    spare_part_quantity: undefined,
                    total_cost: undefined,
                    final_total_cost_in_parent_currency: undefined,
                    final_total_cost_in_pricing_currency: undefined,
                    total_quantity: undefined,
                    unit_cost: undefined,
                    final_total_cost: undefined,
                    total_custom_cost: undefined,
                    total_custom_cost_fixed: undefined,
                    total_custom_cost_percentage: undefined,
                    validation_errors: undefined,
                    custom_costs,
                    notes: undefined,
                    markup_cost_in_pricing_currency: undefined,
                    total_cost_in_pricing_currency: undefined,
                    total_custom_cost_in_pricing_currency: undefined,
                    total_custom_cost_margin_ratio: undefined,
                    unit_cost_in_pricing_currency: undefined,
                };
            }
        }

        // Recursively update child nodes (children_costs array)
        if (updatedNode.children_costs && updatedNode.children_costs.length > 0) {
            updatedNode.children_costs = updatedNode.children_costs.map((child: any) =>
                recurseAndUpdate(child, level + 1)
            );
        }

        // Also handle material_costs if needed
        if (updatedNode.material_costs && updatedNode.material_costs.length > 0) {
            updatedNode.material_costs = updatedNode.material_costs.map((material: any) =>
                recurseAndUpdate(material, level + 1)
            );
        }

        // Return the updated node
        return updatedNode;
    }

    // Start the recursion from the root of the tree and return the new tree
    return recurseAndUpdate(tree);
};

export const addIdForAssembly = (tree: any) => {
    // Recursive function to update and return a new copy of tree nodes
    function recurseAndUpdate(treeNode: any, level = 0): any {
        // Update fields based on the flattened data
        let updatedNode = { ...treeNode };

        if (updatedNode?.hasOwnProperty("assembly_process_costs")) {
            updatedNode = {
                ...updatedNode,
                bom_cost: undefined,
                normal_quantity: undefined,
                override_cost: undefined,
                spare_part_quantity: undefined,
                sum_of_assm_procs_total_cost: updatedNode?.sum_of_assm_procs_total_cost || undefined,
                total_cost: undefined,
                final_total_cost_in_parent_currency: undefined,
                final_total_cost_in_pricing_currency: undefined,
                total_quantity: undefined,
                unit_cost: undefined,
                final_total_cost: undefined,
                sum_of_assm_procs_final_total_cost: undefined,
                sum_of_assm_procs_final_total_cost_in_parent_currency: undefined,
                sum_of_children_final_total_cost_in_parent_currency: undefined,
                sum_of_children_final_total_cost_in_pricing_currency: undefined,
                sum_of_material_final_total_cost_in_parent_currency: undefined,
                markup_cost_in_pricing_currency: undefined,
                total_cost_in_pricing_currency: undefined,
                total_custom_cost_in_pricing_currency: undefined,
                unit_cost_in_pricing_currency: undefined,
                validation_errors: undefined,
                notes: undefined,
                assembly_process_costs: updatedNode.assembly_process_costs?.map((a: any) => {
                    return ({
                        ...a,
                        _id: a?.child_id,
                        markup_percentage: a.markup_percentage || 0,
                        custom_costs: a?.custom_costs?.map((c: CustomCost) => ({ ...c, value: c?.value ?? 0, amount: undefined })) ?? [],
                        assembly_process_costs: a?.assembly_process_costs || undefined,
                        markup_cost: undefined,
                        proc_name: undefined,
                        quantity: undefined,
                        total_cost: undefined,
                        total_custom_cost: undefined,
                        total_custom_cost_fixed: undefined,
                        total_custom_cost_percentage: undefined,
                        unit_cost: undefined,
                        notes: undefined,
                        final_total_cost: undefined,
                        final_total_cost_in_parent_currency: undefined,
                        final_total_cost_in_pricing_currency: undefined,
                        markup_cost_in_pricing_currency: undefined,
                        total_cost_in_pricing_currency: undefined,
                        total_custom_cost_in_pricing_currency: undefined,
                        total_custom_cost_margin_ratio: undefined,
                        unit_cost_in_pricing_currency: undefined,
                    });
                }),
            };
        } else {
            updatedNode = {
                ...updatedNode,
                markup_percentage: updatedNode.markup_percentage || 0,
                custom_costs: updatedNode?.custom_costs?.map((c: CustomCost) => ({ ...c, value: c?.value ?? 0, amount: undefined })) ?? [],
                markup_cost: undefined,
                material_cost: undefined,
                normal_quantity: undefined,
                override_cost: undefined,
                spare_part_quantity: undefined,
                total_cost: undefined,
                final_total_cost_in_parent_currency: undefined,
                final_total_cost_in_pricing_currency: undefined,
                total_quantity: undefined,
                unit_cost: undefined,
                final_total_cost: undefined,
                total_custom_cost: undefined,
                total_custom_cost_fixed: undefined,
                total_custom_cost_percentage: undefined,
                validation_errors: undefined,
                notes: undefined,
                markup_cost_in_pricing_currency: undefined,
                total_cost_in_pricing_currency: undefined,
                total_custom_cost_in_pricing_currency: undefined,
                total_custom_cost_margin_ratio: undefined,
                unit_cost_in_pricing_currency: undefined,
            };
        }

        // Recursively update child nodes (children_costs array)
        if (updatedNode.children_costs && updatedNode.children_costs.length > 0) {
            updatedNode.children_costs = updatedNode.children_costs.map((child: any) =>
                recurseAndUpdate(child, level + 1)
            );
        }

        // Also handle material_costs if needed
        if (updatedNode.material_costs && updatedNode.material_costs.length > 0) {
            updatedNode.material_costs = updatedNode.material_costs.map((material: any) =>
                recurseAndUpdate(material, level + 1)
            );
        }

        // Return the updated node
        return updatedNode;
    }

    // Start the recursion from the root of the tree and return the new tree
    return recurseAndUpdate(tree);
};

export const chartOptions: any = {
    pieHole: 0.4,
    is3D: false,
    backgroundColor: 'transparent',
    legend: {
        position: 'bottom',
        alignment: 'center',
        textStyle: {
            fontSize: 12
        },
        // Ensure full text display
        maxLines: 3,
        width: '100%'
    },
    chartArea: {
        top: '15%',
        width: '100%',
        height: '70%',
    },
    width: '100%',
    height: '400px'
};

export const defaultCostingSummary = [
    {
        label: `Cost Breakup for: `,
        value: "--",
    },
    {
        label: `Total Cost (after tax): `,
        value: `--`,
    },
    {
        label: `Calculated By: `,
        value: "--",
    },
    {
        label: `Calculated At: `,
        value: "--",
    },
    {
        label: `Pricing ID: `,
        value: "--",
    }
];

// Pricing Report Function
export const processBOMDetailsFlatten = (
    bomDetails: any,
    level = "1",
    base_currency: string,
    hasNotes: boolean,
    useMBFormatter: boolean,
    parentQty = 0,
    total_bom_cost = 0
): ProcessedData[] => {
    const flattenedList = [];

    let total_child_bom_cost = 0;
    let total_child_material_cost = 0;
    let total_assembly_cost = 0;

    // Create the parent object
    const parent = {
        column_content: [
            { field: "level", content: `${level}` },
            {
                field: "item_number",
                content: bomDetails.sequence_id,
            },
            {
                field: "version",
                content: `r${bomDetails.version}`,
            },
            {
                field: "description",
                content: `${bomDetails.short_description}${hasNotes && bomDetails?.notes ? `\nNotes: ${bomDetails.notes}` : ""
                    }`,
            },
            {
                field: "unitCost",
                content: formatCurrency(
                    (level === "1"
                        ? total_bom_cost
                        : bomDetails?.final_total_cost_in_pricing_currency?.cost || 0) /
                    (level === "1" ? parentQty : bomDetails.total_quantity || 1),
                    base_currency,
                    useMBFormatter,
                    false
                ),
            },
            {
                field: "qty",
                content: level === "1" ? parentQty : bomDetails.total_quantity || 1,
            },
            {
                field: "totalCost",
                content: formatCurrency(
                    level === "1"
                        ? total_bom_cost
                        : bomDetails?.final_total_cost_in_pricing_currency?.cost || 0,
                    base_currency,
                    useMBFormatter,
                    false
                ),
            },
        ],
        id: bomDetails._id,
    };

    const assembly_process_costs =
        bomDetails?.assembly_process_costs?.map((a: AssemblyProcessCost, idx: number) => {
            total_assembly_cost += a?.final_total_cost_in_pricing_currency?.cost || 0;
            return {
                column_content: [
                    { field: "level", content: "" },
                    {
                        field: "item_number",
                        content: idx + 1,
                    },
                    {
                        field: "version",
                        content: `N/A`,
                    },
                    { field: "description", content: `${a.proc_name}` },
                    {
                        field: "unitCost",
                        content: formatCurrency(
                            (a?.final_total_cost_in_pricing_currency?.cost || 0) /
                            (a.quantity || 1),
                            base_currency,
                            useMBFormatter,
                            false
                        ),
                    },
                    { field: "qty", content: a.quantity || 1 },
                    {
                        field: "totalCost",
                        content: formatCurrency(
                            a?.final_total_cost_in_pricing_currency?.cost || 0,
                            base_currency,
                            useMBFormatter,
                            false
                        ),
                    },
                ],
                id: a.child_id,
            };
        }) || [];

    // Array to hold the immediate children of this parent
    const immediateChildren = [];

    // Process children BOMs recursively
    for (let i = 0; i < (bomDetails?.children_costs || []).length; i++) {
        const childBOM = bomDetails.children_costs[i];
        total_child_bom_cost += childBOM?.final_total_cost_in_pricing_currency?.cost || 0;
        const childBOMDetails = processBOMDetailsFlatten(
            childBOM,
            `${level}.${i + 1}`,
            base_currency,
            hasNotes,
            useMBFormatter
        );
        immediateChildren.push(childBOMDetails[0].parent); // Add the immediate child to the array
        flattenedList.push(...childBOMDetails); // Add the entire flattened structure of the child
    }

    // Process material children (non-recursive)
    for (let i = 0; i < (bomDetails?.material_costs || []).length; i++) {
        const material = bomDetails.material_costs[i];
        total_child_material_cost += material?.final_total_cost_in_pricing_currency?.cost || material.total_cost?.cost || 0;
        const materialChild = {
            parent: {
                column_content: [
                    {
                        field: "level",
                        content: `${level}.${(bomDetails.children_costs?.length || 0) + i + 1
                            }`,
                    },
                    {
                        field: "item_number",
                        content: material.sequence_id,
                    },
                    { field: "version", content: `r${material?.version}` },
                    {
                        field: "description",
                        content: `${material?.short_description}${hasNotes && material?.notes ? `\nNotes: ${material.notes}` : ""
                            }`,
                    },
                    {
                        field: "unitCost",
                        content: formatCurrency(
                            (material?.final_total_cost_in_pricing_currency?.cost ||
                                material.total_cost?.cost ||
                                0) / (material.total_quantity || 1),
                            base_currency,
                            useMBFormatter,
                            false
                        ),
                    },
                    { field: "qty", content: material.total_quantity || 1 },
                    {
                        field: "totalCost",
                        content: formatCurrency(
                            material?.final_total_cost_in_pricing_currency?.cost ||
                            material.total_cost?.cost ||
                            0,
                            base_currency,
                            useMBFormatter,
                            false
                        ),
                    },
                ],
                id: material._id,
            },
            children: [],
        };

        // Add the material child to immediateChildren and flattenedList
        immediateChildren.push(materialChild.parent);
        flattenedList.push(materialChild);
    }

    const chartData = [
        ["Items Breakup", "Value"],
        [`Total Child BOM Cost (${base_currency})`, total_child_bom_cost],
        [`Total Overhead / Activity Breakup Cost (${base_currency})`, total_assembly_cost],
        [`Total Child Material Cost (${base_currency})`, total_child_material_cost],
    ]

    // Add the parent to the flattened list
    flattenedList.unshift({
        parent,
        assembly_process_costs,
        children: immediateChildren,
        totalParentCost:
            level === "1"
                ? total_bom_cost
                : bomDetails?.final_total_cost_in_pricing_currency?.cost || 0,
        chartData
    });

    // Return the flattened list
    return flattenedList as ProcessedData[];
}

type TreeFn = LazyQueryTrigger<QueryDefinition<any, BaseQueryFn<any, unknown, unknown, {}, {}>, "BOMs" | "BOM" | "BOMChildren" | "SearchedBOMs", any, "bomAPI">>;

type PricingUpdateFn = MutationTrigger<MutationDefinition<{
    pricing_id: string;
    bom_id?: string | undefined;
    payload: any;
}, BaseQueryFn<any, unknown, unknown, {}, {}>, "AllPricing" | "AllCosting", any, "pricingAPI">>;

export const pricingOutdatedUpdateFn = async (getBOMTree: TreeFn, updatePricing: PricingUpdateFn, dispatch: AppDispatch, pricing: PricingSliceInterface, id: string, refetch: () => void) => {
    const {
        basic_details,
        exchange_rates,
        cost_groups = [],
        other_costs = [],
        discounts = [],
        taxes = [],
        bom_cost_quantity = 1,
        bom_cost_discount_percentage = 0,
        margin_ratio = 0,
    } = pricing;

    const bom_id = basic_details?.bom_id?._id as string;
    await getBOMTree({ id: bom_id }).unwrap().then(async (res) => {
        const bom_cost = mapTreeToPriceCost(res);

        const unlinks = pricing?.associations?.map(a => ({ object_type: a?.object_type, object_ids: a?.object_details?.map(o => o?._id) })) ?? [];

        await updatePricing({
            pricing_id: id,
            bom_id: basic_details?.bom_id?._id as string,
            payload: {
                bom_cost: bom_cost,
                // bom_id: basic_details?.bom_id?._id as string,
                pricing_name: basic_details.pricing_name,
                pricing_model: basic_details?.pricing_model?.value,
                remarks: basic_details?.remarks,
                exchange_rates: exchange_rates,
                margin_ratio,
                cost_groups: cost_groups?.map((c: CostGroup, idx: number) => ({
                    position: idx + 1,
                    title: c.title,
                    quantity: Number(c.quantity) || 1,
                    discount_percentage: Number(c.discount_percentage) || 0,
                    total_amount: Number(c.total_amount) || 0,
                    // value: c.value,
                    costs: c.costs?.map((cost, idx: number) => ({
                        position: idx + 1,
                        description: cost.description,
                        units: cost.units,
                        value: Number(cost.value) || 0,
                        quantity: Number(cost.quantity) || 0,
                        total_cost: Number(cost.total_cost) || 0
                    })) ?? []
                })) ?? [],
                other_costs: other_costs,
                discounts: discounts,
                taxes: taxes,
                bom_cost_quantity,
                bom_cost_discount_percentage,
                links: [{ object_type: basic_details.object_type?.value as string, object_ids: [basic_details.object_id?.value] }],
                unlinks
            }
        })
            .unwrap()
            .then((res) => {
                enqueueSnackbar(res.message, { variant: "success" });
                refetch();
            })
            .catch((error) => {
                enqueueSnackbar(error?.data?.title ?? "Unable to update the pricing", { variant: "error" });
                console.error(`Error updating pricing: ${JSON.stringify(error)}`)
            })
            .finally(() => {
                dispatch(closeBackdrop());
                dispatch(closeConfirmationDialog());
            })

    })
        .catch((error) => {
            enqueueSnackbar(error?.data?.title ?? "Unable to update the pricing", { variant: "error" });
            console.error(`Error updating pricing: ${JSON.stringify(error)}`);
        })
        .finally(() => {
            dispatch(closeBackdrop());
            dispatch(closeConfirmationDialog());
        });
}

// To get custom costs
export const findFirstCustomCosts = (node: any): any[] | null => {
    // Check assembly_process_costs first
    if (node?.assembly_process_costs?.length > 0) {
        for (const proc of node.assembly_process_costs) {
            if (proc?.custom_costs?.length > 0) {
                return proc.custom_costs;
            }
        }
    }

    // Check material_costs
    if (node?.material_costs?.length > 0) {
        for (const material of node.material_costs) {
            // Check direct custom_costs in material
            if (material?.custom_costs?.length > 0) {
                return material.custom_costs;
            }
            // Recursively check nested structure in material
            const nestedCosts = findFirstCustomCosts(material);
            if (nestedCosts) {
                return nestedCosts;
            }
        }
    }

    // Check children_costs
    if (node?.children_costs?.length > 0) {
        for (const child of node.children_costs) {
            const childCosts = findFirstCustomCosts(child);
            if (childCosts) {
                return childCosts;
            }
        }
    }

    return null;
};

